import {useTranslation} from 'react-i18next';
import {Box, Card, Popper, Stack} from '@mui/material';
import SearchComponent from '@components/inputs/search/Search.component';
import {emitOnce} from '@ngneat/elf';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import React, {useEffect, useState} from 'react';
import NewTableComponent from '@components/table/NewTable.component';
import {useEffectFn} from '@ngneat/effects-hooks';
import {searchClientsContractsEffect} from '@store/clients/contracts/clients-contracts.effects';
import {useObservable} from '@ngneat/react-rxjs';
import {ClientsContract, clientsContractsQuery, clientsContractsService} from '@store/clients/contracts';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import I18n from '@utils/i18n.utils';
import ManageContractDrawer from '@screens/admin/clients/details/contracts/manageContract/ManageContract.drawer';
import ContractServicesModal
  from '@screens/admin/clients/details/contractedServices/manageService/steps/ContractServices.modal';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import {useParams} from 'react-router-dom';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import ClientsServicesTable from '@screens/admin/clients/services/ClientsServices.table';

const ClientsContractsTable = () => {
  const {t} = useTranslation();
  const {clientId, clientUnitId} = useParams();

  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverContract, setPopoverContract] = useState<ClientsContract | null>(null);


  const searchClientsContracts = useEffectFn(searchClientsContractsEffect);

  const [{clientsContracts, loading}] = useObservable(clientsContractsQuery.clientsContracts$);
  const [selectedContracts] = useObservable(clientsContractsQuery.selectedClientsContracts$);

  const [showContract, setShowContract] = useState<ClientsContract | undefined>(undefined);

  const [filters] = useObservable(clientsContractsQuery.filters$);
  const [sortModel] = useObservable(clientsContractsQuery.sort$);
  const [tablePagination] = useObservable(clientsContractsQuery.pagination$);

  const [showAddContract, setShowAddContract] = useState(false);
  const [showContractServices, setShowContractServices] = useState<ClientsContract | undefined>(undefined);

  useEffect(() => {
    if (clientUnitId) {
      clientsContractsService.resetFilters(undefined, clientUnitId);
    } else if (clientId) {
      clientsContractsService.resetFilters(clientId);
    }
  }, [clientUnitId, clientId]);

  useEffect(() => {
    searchClientsContracts({filters, tablePagination, sort: sortModel});
  }, [filters, searchClientsContracts, sortModel, tablePagination]);

  const popoverOpen = Boolean(popoverAnchorEl);

  return (
    <>
      <Stack flex={1} spacing={2} pb="25px">
        <Stack direction="row" justifyContent="flex-end">
          <CheckboxComponent
            label={t('clients.details.contractedServices.manage.selectContract.table.filterExpiredContracts')}
            value={filters.filterExpiredContracts}
            handleChange={clientsContractsService.setFilters('filterExpiredContracts')}
          />
        </Stack>
        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
          <Box width="500px">
            <SearchComponent
              value={filters.search}
              handleChange={(value) => {
                emitOnce(() => {
                  clientsContractsService.setFilters('search')(value);
                  clientsContractsService.setPagination('page')(1);
                });
              }}/>
          </Box>
          <TableFiltersComponent
            filters={filters}
            handleSave={clientsContractsService.setFilters('tableFilters')}
            handleReset={() => {
              if (clientId) {
                clientsContractsService.resetFilters(clientId);
              } else {
                clientsContractsService.resetFilters();
              }

            }}/>
        </Stack>
        <NewTableComponent
          columns={[
            COLUMN_TYPES.TEXT_BOLD({
              field: 'contract.contractReference',
              label: t('clients.details.contractedServices.manage.selectContract.table.reference'),
              valueGetter: (params: GridValueGetterParams<ClientsContract, ClientsContract>) => params.row.contractReference,
              sortable: true,
              width: 150
            }),
            COLUMN_TYPES.TEXT({
              field: 'cOrgUnit.name',
              label: t('clients.details.contractedServices.manage.selectContract.table.clientUnit'),
              valueGetter: (params: GridValueGetterParams<ClientsContract, ClientsContract>) => params.row.clientOrganisationUnitName,
              sortable: true,
              minWidth: 350
            }),
            COLUMN_TYPES.TEXT({
              field: 'contract.contractName',
              label: t('clients.details.contractedServices.manage.selectContract.table.contractName'),
              valueGetter: (params: GridValueGetterParams<ClientsContract, ClientsContract>) => params.row.contractName,
              fullWidth: true,
              sortable: true,
            }),
            COLUMN_TYPES.TEXT_BOLD({
              field: 'waste.name',
              label: t('clients.details.contractedServices.manage.selectContract.table.waste'),
              valueGetter: (params: GridValueGetterParams<any, ClientsContract>) => params.row.services.length ? params.row.services[0].wasteName : '-',
              fullWidth: true,
              sortable: true,
              width: 140
            }),
            COLUMN_TYPES.TEXT_BOLD({
              field: 'container.name',
              label: t('clients.details.contractedServices.manage.selectContract.table.container'),
              valueGetter: (params: GridValueGetterParams<any, ClientsContract>) => params.row.services.length ? params.row.services[0].containerName : '-',
              fullWidth: true,
              sortable: true,
              width: 140
            }),
            COLUMN_TYPES.TEXT_BOLD({
              field: 'ou.name',
              label: t('clients.details.contractedServices.manage.selectContract.table.pickupSupplierUnitName'),
              valueGetter: (params: GridValueGetterParams<any, ClientsContract>) => params.row.pickupSupplierUnitName || '-',
              fullWidth: true,
              sortable: false,
              width: 140
            }),
            COLUMN_TYPES.TEXT({
              field: 'contract.startDate',
              label: t('clients.details.contractedServices.manage.selectContract.table.startDate'),
              valueFormatter: (params) => params.value ? I18n.t('global.date', {date: new Date(params.value)}) : '',
              sortable: true,
              width: 140
            }),
            COLUMN_TYPES.TEXT({
              field: 'contract.endDate',
              label: t('clients.details.contractedServices.manage.selectContract.table.endDate'),
              valueFormatter: (params) => params.value ? I18n.t('global.date', {date: new Date(params.value)}) : '',
              sortable: true,
              width: 130
            }),
            COLUMN_TYPES.TEXT({
              field: 'invoiceAddress.addressCode',
              label: t('clients.details.contractedServices.manage.selectContract.table.invoiceAddressCode'),
              valueGetter: (params: GridValueGetterParams<any, ClientsContract>) => params.row.invoiceAddress ? params.row.invoiceAddress.addressCode : '-',
              minWidth: 50,
              sortable: false
            }),
            COLUMN_TYPES.TEXT_WITH_ACTION({
              field: 'servicesCount',
              actionLabel: (contract: ClientsContract) => contract.servicesCount,
              handleClick: (contract: ClientsContract) => setShowContractServices(contract),
              handleMouseOver: (contract: ClientsContract, event) => {
                setPopoverAnchorEl(event.currentTarget);
                setPopoverContract(contract);
              },
              handleMouseOut: (contract, event) => {
                setPopoverAnchorEl(null);
                setPopoverContract(null);
              },
              label: t('clients.details.contractedServices.manage.selectContract.table.servicesCount'),
              sortable: false,
              width: 100
            }),
            COLUMN_TYPES.ACTIONS({
              handleShowRow: (contract: ClientsContract) => {
                setShowContract(contract);
                setShowAddContract(true);
              },
            }),
          ]}
          getRowClassName={(params) => {
            if (params.row.endDate && new Date(params.row.endDate) < new Date()) {
              return 'grey-row';
            }
            return '';
          }}
          loading={loading}
          handleReload={() => searchClientsContracts({filters, tablePagination, sort: sortModel})}
          handlePageSizeChange={clientsContractsService.setPagination('limit')}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => clientsContractsService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          handleSortModelChange={clientsContractsService.setSort}
          sortModel={[sortModel]}
          handleRowSelected={clientsContractsService.setSelectedClientsContracts}
          rowsSelected={selectedContracts}
          totalRows={clientsContractsQuery.totalRows}
          data={clientsContracts}
        />
      </Stack>
      {showAddContract && (
        <ManageContractDrawer
          handleClose={() => setShowAddContract(false)}
          handleCreated={() => {
            searchClientsContracts({filters, tablePagination, sort: sortModel});
          }}
          manageContract={showContract ? ClientsContract.toManageClientsContract(showContract) : undefined}
          isCreation={!showContract}
        />
      )}
      {!!showContractServices && (
        <ContractServicesModal
          contract={showContractServices}
          contractId={showContractServices.id}
          handleClose={() => setShowContractServices(undefined)}/>
      )}
      <Popper open={popoverOpen} anchorEl={popoverAnchorEl}>
        <Card>
          <ClientsServicesTable showRowsOnly={true} contractIds={popoverContract?.id ? [popoverContract?.id] : []}/>
        </Card>
      </Popper>
    </>
  );
};

export default ClientsContractsTable;
